/**
 * CSV export for the Agreements tab.
 *
 * Pure functions with no DOM and no React so the escaping rules can be tested
 * exhaustively — the download plumbing is the easy part, the escaping is where a
 * quiet wrong answer comes from.
 *
 * Three decisions worth knowing before changing anything here:
 *
 * 1. ONE ROW PER AGREEMENT — changed 2026-08-28, feedback item 38. Stacie asked
 *    for it directly ("Need one row per agreement") and the reason is what a
 *    spreadsheet does with the file: at rate grain you cannot count agreements. A
 *    pivot by customer counts rates and calls them agreements, and every
 *    agreement-level question — how many are in renewal, how many have no rate on
 *    file — silently over-reports by the number of multi-rate agreements.
 *
 *    The PREVIOUS rule was one row per rate, and its reasoning was right about the
 *    thing it was protecting: picking a single rate to represent an agreement is
 *    the mistake the Hotels panel was corrected for on 2026-08-09, where the
 *    "best" rate turned out to be selected by CONTACT quality and was therefore
 *    incidental. That constraint is KEPT. Nothing here picks a representative
 *    rate.
 *
 *    Instead every per-rate column carries ALL of the agreement's rates, joined
 *    with '; ' in the order the API returned them, and the lists are POSITIONALLY
 *    ALIGNED: index i of `Rate amounts` belongs with index i of `Rate types`,
 *    `Seasons`, `Currencies` and the rest. A null for one rate is an empty slot,
 *    not a dropped element, so the positions cannot drift. A single-rate agreement
 *    — the common case — renders exactly as it did before.
 *
 *    Two DERIVED columns answer the question the lists cannot: `Coverage starts`
 *    and `Coverage ends` are the earliest and latest rate dates on the agreement,
 *    which is a fact about the agreement rather than a chosen rate, and is what
 *    "when does this lapse" actually needs.
 *
 *    An agreement with no rates still emits its row with the rate columns blank —
 *    "no rate recorded" is itself reportable, and for a Sourcing queue it is the
 *    normal state.
 *
 * 2. Formula injection is escaped. These cells carry hotel names, contact emails
 *    and free-text notes that originate outside this system, and the file's whole
 *    purpose is to be opened in Excel — where a cell beginning `=`, `+`, `-`, `@`
 *    or a control character is evaluated as a formula. Prefixing with a tab-free
 *    apostrophe would be visible in the data; prefixing with `'` inside a quoted
 *    field is the conventional neutralisation and is what Excel strips on display.
 *
 * 3. Decimal values pass through as the STRINGS the API sent. They are Postgres
 *    NUMERICs stringified at the API boundary precisely so a float round-trip
 *    cannot alter a negotiated rate or a commission percentage; parsing them here
 *    to re-format would give that back.
 */
import type { Agreement } from '../pages/agreements/AgreementDetail';

/** One output column: a header and how to pull it off an (agreement, rate) pair. */
export interface CsvColumn {
  header: string;
  value: (row: AgreementCsvRow) => string | null | undefined;
}

/**
 * One agreement and ALL of its rates. `rates` is empty for an agreement with no
 * rates recorded, which still gets a row.
 */
export interface AgreementCsvRow {
  agreement: Agreement;
  /** Every rate, in the order the API returned them. Never narrowed to one. */
  rates: readonly Agreement['rates'][number][];
  /** `rates.length`, exported as its own column so a reader can see the count. */
  rateCount: number;
}

/** One output column: a header and how to pull it off a row. */
type RateField = keyof Agreement['rates'][number];

/**
 * Join one field across every rate on the agreement, preserving POSITION.
 *
 * A missing value becomes an empty slot rather than being filtered out — dropping
 * it would shift every later element and silently re-pair `Rate amounts` with the
 * wrong `Seasons`. That is the failure mode this whole file exists to prevent:
 * a file that opens fine and is wrong.
 *
 * Returns null (an empty cell) when NO rate has the field, so a rateless
 * agreement and an agreement whose rates all lack a season both read as blank
 * rather than as a row of lone semicolons.
 */
function joinRateField(
  rates: readonly Agreement['rates'][number][],
  field: RateField,
): string | null {
  if (rates.length === 0) return null;
  const parts = rates.map((r) => {
    const v = r[field];
    return v == null ? '' : String(v);
  });
  return parts.some((p) => p !== '') ? parts.join('; ') : null;
}

/**
 * The earliest / latest rate date on the agreement.
 *
 * Compared as strings, which is correct ONLY for the ISO-8601 the API sends
 * (`2026-01-01T00:00:00.000Z`) — lexicographic order and chronological order
 * coincide there. Anything not matching that shape is IGNORED rather than
 * compared, because a wrong min/max here is a confident wrong answer about when
 * an agreement lapses, which is worse than a blank cell.
 */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;

function rateDateBound(
  rates: readonly Agreement['rates'][number][],
  field: RateField,
  pick: 'min' | 'max',
): string | null {
  const values = rates
    .map((r) => r[field])
    .filter((v): v is string => typeof v === 'string' && ISO_DATE.test(v));
  if (values.length === 0) return null;
  return values.reduce((acc, v) =>
    pick === 'min' ? (v < acc ? v : acc) : v > acc ? v : acc,
  );
}

/**
 * The exported columns.
 *
 * Deliberately WIDER than the on-screen table. The table is for scanning and
 * comparing, so it stays at the dozen columns you sort on; the export is for
 * auditing a book of business in a spreadsheet, which is where the ids, the
 * emails, the GDS codes and the full rate detail become the point. Both the
 * customer NAME and the customer ID are present because the name is what a human
 * recognises and the id is what joins back to any other system.
 */
export const AGREEMENT_CSV_COLUMNS: CsvColumn[] = [
  { header: 'Customer', value: (r) => r.agreement.customer?.name ?? null },
  { header: 'Customer ID', value: (r) => r.agreement.customerId },
  { header: 'Hotel', value: (r) => r.agreement.property?.propertyName },
  { header: 'City', value: (r) => r.agreement.property?.propertyCity },
  { header: 'State', value: (r) => r.agreement.property?.propertyState },
  { header: 'Chain', value: (r) => r.agreement.property?.vendorFamily },
  { header: 'TMC', value: (r) => r.agreement.tmc },
  { header: 'Status', value: (r) => r.agreement.status },
  { header: 'Owner team', value: (r) => r.agreement.ownerTeam },
  { header: 'Owner email', value: (r) => r.agreement.ownerEmail },
  { header: 'Hotel contact email', value: (r) => r.agreement.hsmEmail },
  { header: 'Hotel POC email', value: (r) => r.agreement.hotelPocEmail },
  // All three GDS codes from hotel_master, plus the pair recorded for THIS
  // customer below. Both sides are exported on purpose: a mismatch between the
  // master record and the customer's own code is one of the first things Supplier
  // Relations checks when a rate will not load, and you cannot compare two values
  // when the file only carries one of them.
  {
    header: 'Sabre code (master)',
    value: (r) => r.agreement.hotelMaster?.sabrePropertyCode ?? null,
  },
  {
    header: 'Travelport code (master)',
    value: (r) => r.agreement.hotelMaster?.travelportCode ?? null,
  },
  {
    header: 'Worldspan code (master)',
    value: (r) => r.agreement.hotelMaster?.worldspanCode ?? null,
  },
  {
    header: 'GDS code (this customer)',
    value: (r) => r.agreement.property?.gdsPropertyCode,
  },
  {
    header: 'Vendor code (this customer)',
    value: (r) => r.agreement.property?.vendorCode,
  },
  { header: 'Term starts', value: (r) => r.agreement.termStart },
  { header: 'Term ends', value: (r) => r.agreement.termEnd },
  // ── Rate columns, at AGREEMENT grain ────────────────────────────────────
  // The count comes FIRST so a reader knows how many values to expect in the
  // lists that follow.
  { header: 'Rates on agreement', value: (r) => String(r.rateCount) },
  // Each of these carries every rate, '; '-joined in API order, and the lists are
  // positionally aligned with each other — see joinRateField. Headers are PLURAL
  // to say so: a column called "Rate amount" holding "189.00; 205.00" invites
  // someone to sum the cell.
  { header: 'Rate types', value: (r) => joinRateField(r.rates, 'rateType') },
  { header: 'Rate amounts', value: (r) => joinRateField(r.rates, 'amount') },
  { header: 'Currencies', value: (r) => joinRateField(r.rates, 'currency') },
  {
    header: 'Commission %',
    value: (r) => joinRateField(r.rates, 'commissionPct'),
  },
  {
    header: 'Availability',
    value: (r) => joinRateField(r.rates, 'availabilityType'),
  },
  { header: 'Seasons', value: (r) => joinRateField(r.rates, 'season') },
  {
    header: 'Rate effective from',
    value: (r) => joinRateField(r.rates, 'effectiveFrom'),
  },
  {
    header: 'Rate effective to',
    value: (r) => joinRateField(r.rates, 'effectiveTo'),
  },
  {
    header: 'GDS loaded statuses',
    value: (r) => joinRateField(r.rates, 'gdsLoadedStatus'),
  },
  // DERIVED, and the reason the row is worth having at this grain: the span the
  // agreement's rates actually cover. A fact about the agreement, not a rate
  // someone picked.
  {
    header: 'Coverage starts',
    value: (r) => rateDateBound(r.rates, 'effectiveFrom', 'min'),
  },
  {
    header: 'Coverage ends',
    value: (r) => rateDateBound(r.rates, 'effectiveTo', 'max'),
  },
  // RACs live on the load rows, and one agreement can load into several GDSes.
  // Joined with '; ' rather than ',' so the cell does not need its own parsing.
  {
    header: 'RACs',
    value: (r) =>
      r.agreement.loads
        ?.map((l) => l.rac)
        .filter((rac): rac is string => Boolean(rac && rac.trim()))
        .join('; ') || null,
  },
  { header: 'Last updated', value: (r) => r.agreement.updatedAt },
];

/**
 * One row per agreement — item 38. Never fewer (a rateless agreement still gets
 * its row) and never more (a seven-rate agreement is still one row).
 *
 * Rate order inside each row is whatever the API returned (earliest expiry
 * first), which keeps the export's ordering the same as the detail panel's. The
 * `?? []` is defensive because the payload is JSON off the wire: `rates` absent
 * would otherwise throw inside a download click handler, where the error surfaces
 * as nothing happening at all.
 */
export function agreementCsvRows(
  agreements: readonly Agreement[],
): AgreementCsvRow[] {
  return agreements.map((agreement) => {
    const rates = agreement.rates ?? [];
    return { agreement, rates, rateCount: rates.length };
  });
}

/** Characters Excel and Sheets treat as the start of a formula. */
const FORMULA_LEAD = /^[=+\-@\t\r]/;

/**
 * A plain number, including a negative one.
 *
 * Exempted from formula-neutralisation, and the exemption is the whole reason this
 * pattern exists: `-` is a formula lead, so neutralising it blindly would turn
 * every negative amount into `'-42.00` — TEXT in Excel. A rate or commission
 * column where some cells are numbers and some are text does not sum, does not
 * sort, and does not average, which for an export whose purpose is auditing rates
 * is a worse outcome than the injection risk it was guarding against. `-1+1+cmd`
 * and friends do not match this, so they are still neutralised.
 */
const PLAIN_NUMBER = /^-?\d+(\.\d+)?$/;

/**
 * Escape one cell.
 *
 * Order matters: neutralise the formula lead FIRST, then quote. Quoting first
 * would put the `"` ahead of the `=`, and a spreadsheet strips the quoting before
 * evaluating the content — so the formula would survive.
 *
 * null/undefined become empty, NOT the string "null". A literal "null" in a
 * spreadsheet column is indistinguishable from a hotel genuinely named that, and
 * it breaks every numeric column it lands in.
 */
export function csvCell(value: string | null | undefined): string {
  if (value == null) return '';
  const raw = String(value);
  if (raw === '') return '';

  const neutralised =
    FORMULA_LEAD.test(raw) && !PLAIN_NUMBER.test(raw) ? `'${raw}` : raw;

  // Quote when the value contains a delimiter, a quote, or any newline. Doubling
  // the inner quotes is RFC 4180's escape.
  if (/[",\r\n]/.test(neutralised)) {
    return `"${neutralised.replace(/"/g, '""')}"`;
  }
  return neutralised;
}

/**
 * Build the CSV body.
 *
 * CRLF line endings because that is what RFC 4180 specifies and what Excel on
 * Windows expects; a lone LF renders as one giant line in some versions.
 */
export function agreementsCsv(
  agreements: readonly Agreement[],
  columns: readonly CsvColumn[] = AGREEMENT_CSV_COLUMNS,
): string {
  const lines: string[] = [columns.map((c) => csvCell(c.header)).join(',')];
  for (const row of agreementCsvRows(agreements)) {
    lines.push(columns.map((c) => csvCell(c.value(row))).join(','));
  }
  return lines.join('\r\n');
}

/**
 * What the UI must tell the user before they trust the file.
 *
 * `complete` is false when the query hit the API's row cap, in which case the
 * export holds the fetched page and NOT the full result. That is the failure this
 * whole export was built carefully around: a CSV of 500 rows out of 812 looks
 * complete, gets mailed to a hotel or used to true up a commission, and nothing
 * about the file says it was cut off.
 */
export interface ExportSummary {
  /**
   * Rows the file will contain, excluding the header.
   *
   * Since item 38 this always equals `agreements` — one row each. Both fields are
   * kept, and a test pins the equality, so that if the grain is ever changed again
   * the summary the UI prints stays true instead of quietly becoming a lie. It is
   * the row COUNT that the reader is being promised, not a restatement of the
   * agreement count.
   */
  rows: number;
  /** Agreements those rows came from. Equal to `rows` at agreement grain. */
  agreements: number;
  /** True when the fetched page is the whole result set. */
  complete: boolean;
  /** Rows the filter matches in total, when the caller knows it. */
  total: number | null;
}

export function exportSummary(
  agreements: readonly Agreement[],
  total: number | null,
): ExportSummary {
  return {
    rows: agreementCsvRows(agreements).length,
    agreements: agreements.length,
    // Unknown total means we cannot claim completeness — so we don't. `null` is
    // treated as "assume complete" nowhere in here.
    complete: total == null ? true : agreements.length >= total,
    total,
  };
}

/**
 * A filename that says what the file contains.
 *
 * Dated because these get saved, mailed and compared, and an `agreements.csv`
 * sitting in a Downloads folder next to three others is unidentifiable. The stage
 * and the customer count are in the name for the same reason — the alternative is
 * opening the file to find out which queue it was.
 */
export function csvFilename(
  stage: string,
  customerCount: number,
  now: Date = new Date(),
): string {
  const date = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');
  const scope = customerCount > 0 ? `-${customerCount}customer` : '';
  const plural = customerCount > 1 ? 's' : '';
  return `agreements-${stage}${scope}${plural}-${date}.csv`;
}
